import React, { useState, SyntheticEvent, useEffect } from 'react'
import qs from 'query-string'
import { useDispatch, useSelector } from 'react-redux'
import { Route, useRouteMatch } from 'react-router-dom'
import { useHistoryPushWithForwardedParams, useNewTranslation } from 'hooks'
import styled from 'particles/themes/styled'
import TemplateSelector from './TemplateSelector'
import TemplateDetail from './TemplateDetail'
import NavBar from './NavBar'
import { TabOption } from 'particles/Tab'
import Toast from 'particles/Toast'
import { selectTemplateData } from 'state/campaignTemplates'
import TemplatePaymentSummary from './TemplateSummary'
import { selectTemplateFormErrors } from 'state/error/campaignTemplates/selectors'
import { resetTemplateFormError } from 'state/error/campaignTemplates'
import { getCampaign, setEditingCampaign } from 'state/campaigns'
import { getCampaignFieldsComments } from 'state/comments'
import { selectCampaignByTemplateData } from 'state'
import { useCheckEditTemplatedCampaignPermission } from 'hooks/permissions'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import { setConfirmPopup } from 'state/confirmPopup'

export const TemplateContainer = styled.div`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    height: 100vh;
    max-height: 100vh;
    overflow: auto;
    background-color: ${({ theme }) => theme.colors.background.secondary};
    .nav-bar {
        padding: 24px 32px 0;
        @media screen and (max-width: 768px) {
            padding: 122px 16px 0 16px;
        }
    }
    .template-content {
        padding: 0px 32px;
        height: 100%;
        overflow: hidden;
        @media screen and (min-width: 768px) and (max-width: 1024px) {
            padding: 0 0px 0px 32px;
        }
        @media screen and (max-width: 768px) {
            padding: 0 16px;
        }
    }
`

export type TEMPLATE_TAB_ID = 'templateSelector' | 'templateDetail' | 'templateSummary'

const Template: React.FC = () => {
    const [t] = useNewTranslation()
    const match = useRouteMatch()
    const historyPush = useHistoryPushWithForwardedParams()
    const prefix = match && match.path
    const params = qs.parse(location.search)
    const { templateData: templateDataUuid, campaign: campaignUuidFromUrl } = params
    const templateData = useSelector(selectTemplateData)
    const [openInfoToast, setOpenInfoToast] = useState(false)
    const [infoToastMessage, setInfoToastMessage] = useState('')
    const dispatch = useDispatch()
    const templateError = useSelector(selectTemplateFormErrors)
    const campaign = useSelector(selectCampaignByTemplateData)
    const isPermissionEditable = useCheckEditTemplatedCampaignPermission((campaign && campaign.status) || undefined)
    const isReadOnly = !isPermissionEditable
    const {
        campaign_info: errCampaignInfo,
        campaign_schedule: errCampaignSchedule,
        task_element_data: errTaskElements,
        time_window: errTimeWindow,
        brand: errBrand,
    } = templateError
    const isTemplateSelectorError = !!errBrand
    const isCampaignScheduleError = !!errCampaignSchedule || !!errTimeWindow
    const campaignUuid = templateData?.campaign?.uuid ? templateData?.campaign?.uuid : campaignUuidFromUrl

    useEffect(() => {
        if (typeof campaignUuid === 'string' && campaignUuid) {
            dispatch(setEditingCampaign({ uuid: campaignUuid }))
            dispatch(getCampaign({ uuid: campaignUuid }))
            dispatch(getCampaignFieldsComments({ uuid: campaignUuid }))
        }
    }, [dispatch, campaignUuid])

    const enableTemplateDetail = templateData !== undefined
    const TAB_URL = {
        selector: templateDataUuid
            ? `${prefix}/templateSelector?templateData=${templateDataUuid}&campaign=${campaignUuid}`
            : `${prefix}/templateSelector`,
        detail: templateDataUuid
            ? `${prefix}/templateDetail?templateData=${templateDataUuid}&campaign=${campaignUuid}`
            : `${prefix}/templateDetail`,
        summary: templateDataUuid
            ? `${prefix}/templateSummary?templateData=${templateDataUuid}&campaign=${campaignUuid}`
            : `${prefix}/templateSummary`,
    }
    const initialTab = (() => {
        if (TAB_URL.summary.includes(location.pathname)) {
            return 'templateSummary'
        }

        if (TAB_URL.detail.includes(location.pathname)) {
            return 'templateDetail'
        }

        return 'templateSelector'
    })()

    const [selectedTab, setSelectedTab] = useState<TEMPLATE_TAB_ID>(initialTab)

    useEffect(() => {
        // reset state error from previous session when open new template from campaign list
        dispatch(resetTemplateFormError())
    }, [])

    useEffect(() => {
        if (!campaign && templateData?.campaign.uuid) {
            dispatch(
                getCampaign({
                    uuid: templateData.campaign.uuid,
                }),
            )
        }
    }, [templateData?.campaign.uuid])

    const onClickStep1 = () => {
        setSelectedTab('templateSelector')
        historyPush(TAB_URL.selector)
    }

    const onClickStep2 = () => {
        if (!enableTemplateDetail) {
            setInfoToastMessage(t('campaign.template.taost.notSelectedTemplate.text'))
            setOpenInfoToast(true)
        } else {
            setSelectedTab('templateDetail')
            historyPush(TAB_URL.detail)
        }
    }

    const onClickStep3 = () => {
        if (!enableTemplateDetail) {
            setInfoToastMessage(t('campaign.template.taost.notSelectedTemplate.text'))
            setOpenInfoToast(true)
            return
        }
        if (isReadOnly) {
            setSelectedTab('templateSummary')
            historyPush(TAB_URL.summary)
            return
        }
        setSelectedTab('templateSummary')
        historyPush(TAB_URL.summary)
    }

    const onClickTab = (callback: VoidFunction) => {
        const isTemplateValueChange = localStorage.getItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
        if (isTemplateValueChange === 'yes') {
            dispatch(
                setConfirmPopup({
                    show: true,
                    onConfirm: callback,
                }),
            )
        } else {
            callback()
        }
    }

    const tabOptions: TabOption[] = [
        {
            id: 'templateSelector',
            label: t('campaign.template.step1.title'),
            disabled: false,
            isError: isTemplateSelectorError,
            onClick: () => {
                onClickTab(onClickStep1)
            },
        },
        {
            id: 'templateDetail',
            label: t('campaign.template.step2.title'),
            disabled: !enableTemplateDetail,
            isError: !!errCampaignInfo || !!isCampaignScheduleError || !!errTaskElements,
            onClick: () => {
                onClickTab(onClickStep2)
            },
        },
        {
            id: 'templateSummary',
            label: t('campaign.template.step3.title'),
            disabled: !enableTemplateDetail && !templateDataUuid,
            onClick: () => {
                onClickTab(onClickStep3)
            },
        },
    ]

    const handleClose = (event: SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return
        }

        setOpenInfoToast(false)
    }

    return (
        <TemplateContainer>
            <div className="nav-bar">
                <NavBar selectTab={selectedTab} tabOptions={tabOptions} isDisabledClickable={true} />
            </div>
            <div className="template-content">
                <Route
                    exact
                    path={`${prefix}/templateSummary`}
                    render={props => <TemplatePaymentSummary {...props} setSelectTab={setSelectedTab} />}
                />
                <Route
                    exact
                    path={`${prefix}/templateDetail`}
                    render={() => <TemplateDetail setSelectedTab={setSelectedTab} />}
                />
                <Route
                    exact
                    path={`${prefix}/templateSelector`}
                    render={props => (
                        <TemplateSelector
                            {...props}
                            setOpenInfoToast={setOpenInfoToast}
                            setInfoToastMessage={setInfoToastMessage}
                            setSelectTab={setSelectedTab}
                        />
                    )}
                />
            </div>
            <Toast message={infoToastMessage} open={openInfoToast} onClose={handleClose} />
        </TemplateContainer>
    )
}

export default Template
